import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import {
	computeInitialLiftYFromWidth,
	createInitialItems,
	createLaneGeometry,
	createRng,
	getInboardOffsetPx,
	getSideElementSizePx,
	getSideElementsTopPct,
	normalizeCountPerSide,
	wrapAndAdvanceItems,
} from '@/features/laneChooser/helpers';
import { useLaneChooserTheme } from '@/features/laneChooser/theme';

import type { SideElementItem } from '@/features/laneChooser/helpers';

export const MIN_SCALE_AT_TOP = 0.5; // scale at the top edge (farthest)
export const SCALE_SPAN_TO_BOTTOM = 0.6; // additional scale added by the time item reaches the bottom

export type SideElementsProps = {
	className?: string;
	style?: React.CSSProperties;
	topWidthPct: number;
	bottomWidthPct: number;
	side?: 'left' | 'right' | 'both';
	motionScale?: number;
	motionDirection?: 1 | -1; // 1 = downstream, -1 = upstream
	bottomTargetY?: number;
	paused?: boolean;
	countPerSide?: 1 | 2;
};

export const SideElements = memo(function SideElements({
	className,
	style,
	topWidthPct,
	bottomWidthPct,
	side = 'both',
	motionScale = 1,
	motionDirection = 1,
	bottomTargetY,
	paused = false,
	countPerSide = 2,
}: SideElementsProps) {
	// Measure wrapper size for container (used for geometry calculations)
	const hostRef = useRef<HTMLDivElement | null>(null);
	const [vb, setVb] = useState<{ w: number; h: number }>({ w: 1, h: 1 });

	useEffect(() => {
		const el = hostRef.current;
		if (!el) return;
		const update = () => {
			const r = el.getBoundingClientRect();
			setVb({ w: Math.max(1, r.width), h: Math.max(1, r.height) });
		};
		update();
		let ro: ResizeObserver | null = null;
		if (typeof ResizeObserver !== 'undefined') {
			ro = new ResizeObserver(() => update());
			ro.observe(el);
		} else {
			window.addEventListener('resize', update);
		}
		return () => {
			if (ro) ro.disconnect();
			else window.removeEventListener('resize', update);
		};
	}, []);

	const vbWidth = vb.w;
	const vbHeight = vb.h;
	const elementSize = getSideElementSizePx();
	const inboardOffset = getInboardOffsetPx();
	const itemsPerSide = normalizeCountPerSide(countPerSide);
	const theme = useLaneChooserTheme();
	const sideSprites = theme.images.sideElements;

	const geometry = useMemo(
		() =>
			createLaneGeometry({
				width: vbWidth,
				height: vbHeight,
				topWidthPct: getSideElementsTopPct(),
				bottomWidthPct,
				elementSize,
			}),
		[vbWidth, vbHeight, bottomWidthPct, elementSize],
	);
	const { bankXAtY, loopMin, loopMax, loopSpan, viewHeight } = geometry;

	const baseSpeedPxPerSec = useMemo(() => {
		const secs = 28;
		const lift = Math.abs(computeInitialLiftYFromWidth());
		const travel = bottomTargetY != null ? bottomTargetY + lift : viewHeight;
		return Math.max(1, travel / Math.max(1, secs));
	}, [bottomTargetY, viewHeight]);

	const rng = useMemo(() => createRng(), []);

	const initialItems = useMemo(
		() =>
			createInitialItems({
				sides: side,
				geometry,
				itemsPerSide,
				elementSize,
				rng,
				sprites: sideSprites,
			}),
		[geometry, side, itemsPerSide, elementSize, rng, sideSprites],
	);

	const [items, setItems] = useState<SideElementItem[]>(initialItems);
	useEffect(() => setItems(initialItems), [initialItems]);

	const lastTsRef = useRef<number | null>(null);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		const tick = (ts: number) => {
			if (lastTsRef.current == null) lastTsRef.current = ts;
			const dt = (ts - lastTsRef.current) / 1000;
			lastTsRef.current = ts;
			if (!paused) {
				setItems((prev) =>
					wrapAndAdvanceItems({
						items: prev,
						motionScale,
						motionDirection,
						baseSpeedPxPerSec,
						deltaTime: dt,
						rng,
						loopMin,
						loopMax,
						loopSpan,
						sprites: sideSprites,
					}),
				);
			} else {
				// Avoid large jumps after resuming
				lastTsRef.current = ts;
			}
			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
			lastTsRef.current = null;
		};
	}, [baseSpeedPxPerSec, motionDirection, loopMax, loopMin, loopSpan, paused, rng, motionScale, sideSprites]);

	return (
		<div ref={hostRef} className={classNames('lane-chooser__elements', className)} style={style} aria-hidden>
			{items.map((it) => {
				const y = it.y;
				const leftBankX = bankXAtY(y, 'left');
				const rightBankX = bankXAtY(y, 'right');
				const towardCenter = it.xSide === 'left' ? 1 : -1;
				let x = (it.xSide === 'left' ? leftBankX : rightBankX) + towardCenter * inboardOffset + it.jitter;
				const half = elementSize / 2;
				const minX = leftBankX + half;
				const maxX = rightBankX - half;
				x = Math.max(minX, Math.min(maxX, x));
				return (
					<img
						key={`${it.id}:${it.image}`}
						src={it.image}
						alt=""
						className="lane-chooser__side-image"
						style={{
							left: x - half,
							top: y - half,
							width: elementSize,
							height: elementSize,
							transform: `scale(${MIN_SCALE_AT_TOP + SCALE_SPAN_TO_BOTTOM * (y / viewHeight)})`,
						}}
					/>
				);
			})}
		</div>
	);
});

export default SideElements;
